/**
 * The client stays selected across every section (docs/client-context.md).
 *
 * The current client is `?clientId=` on the current URL, and nothing else: no React context holds a second copy that
 * could disagree with the address bar. These helpers say which screens carry it, and build a link that keeps it.
 */

/** Every route whose page reads `?clientId=`. The nav carries the client to these, and to nothing else. */
export const CLIENT_SCOPED_PATHS = [
  // Overview is the client's dashboard (Christian's walk of deploy #20), scoped to the header pick like Accuracy.
  '/',
  '/clients',
  '/bank-transactions',
  '/account-transactions',
  '/journals',
  '/gl-mappings',
  '/extraction-accuracy',
] as const;

/**
 * Screens that read `clientId` but are NOT client screens: a hand-off for one record of one client (View document opens
 * one upload's file). The header's pick never drives them, because a client switch must never re-point a link that names
 * a specific document. Listed here, so the client-context test can tell an exception from a forgotten scope.
 */
export const CLIENT_HANDOFF_PATHS = ['/uploads/view'] as const;

export function isClientScoped(pathname: string): boolean {
  return (CLIENT_SCOPED_PATHS as readonly string[]).includes(pathname);
}

/** `path?clientId=<id>` for a client-scoped path with a client; the bare path otherwise. Only `clientId` travels. */
export function withClientId(path: string, clientId: string): string {
  if (!clientId || !isClientScoped(path)) return path;
  return `${path}?${new URLSearchParams({ clientId }).toString()}`;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Only a Dataverse account id is ever remembered (the server refuses anything else, too). */
export function isClientId(value: string): boolean {
  return GUID.test(value);
}
